import { useEffect, useId, useRef } from "react";

/**
 * Reusable inline HubSpot form embed. Loads the HubSpot forms script once
 * and creates the form inside a stable, hydration-safe container.
 */

declare global {
  interface Window {
    hbspt?: any;
  }
}

const DEFAULT_SCRIPT_SRC = "https://js.hsforms.net/forms/embed/v2.js";
const DEFAULT_PORTAL_ID = "7030766";
const DEFAULT_REGION = "na1";

interface HubSpotInlineFormProps {
  formId: string;
  portalId?: string;
  region?: string;
  targetId?: string;
  onFormSubmitted?: () => void;
}

export function HubSpotInlineForm({
  formId,
  portalId = DEFAULT_PORTAL_ID,
  region = DEFAULT_REGION,
  targetId: targetIdProp,
  onFormSubmitted,
}: HubSpotInlineFormProps) {
  const generatedId = `hubspot-inline-form-${useId().replace(/:/g, "-")}`;
  const containerId = targetIdProp || generatedId;
  const onFormSubmittedRef = useRef(onFormSubmitted);
  onFormSubmittedRef.current = onFormSubmitted;

  useEffect(() => {
    let cancelled = false;

    const createForm = () => {
      if (cancelled || !window.hbspt) return;
      const el = document.getElementById(containerId);
      if (el) el.innerHTML = "";
      try {
        window.hbspt.forms.create({
          region,
          portalId,
          formId,
          target: `#${containerId}`,
          onFormSubmitted: onFormSubmittedRef.current
            ? () => {
                onFormSubmittedRef.current?.();
              }
            : undefined,
        });
      } catch (e) {
        console.error("HubSpot inline form load error:", e);
      }
    };

    if (window.hbspt) {
      createForm();
      return () => {
        cancelled = true;
      };
    }

    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${DEFAULT_SCRIPT_SRC}"]`
    );
    if (!script) {
      script = document.createElement("script");
      script.src = DEFAULT_SCRIPT_SRC;
      script.async = true;
      script.charset = "utf-8";
      script.type = "text/javascript";
      document.body.appendChild(script);
    }
    script.addEventListener("load", createForm);

    return () => {
      cancelled = true;
      script?.removeEventListener("load", createForm);
    };
  }, [formId, portalId, region, containerId]);

  return <div id={containerId} />;
}

export default HubSpotInlineForm;
